// @ts-nocheck
import Editor from './../../../../../components/Editor/Editor';
import {
  Clock,
  FileText,
  MapPin,
  AirplaneTakeoff
} from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { formatTimeLocale } from '../../../../../utils/dateUtility';
import { useTimeFormat } from '../../../../../hooks/useTimeFormat';
import { openMaps } from '../../../helpers/contact';
import * as PropTypes from 'prop-types';

/**
 * A service card component for flights.
 *
 * @param {Object} servicefields - the fields of the service
 * @param {string} tripdetaileddayserviceid - the id of the tripdetaileddayservice
 * @param {boolean} isPreview -  whether the service card is in preview mode
 * @param {boolean} isDesktop - whether the view is in desktop mode
 * @returns {JSX.Element}
 */
const FlightCard = ({
  servicefields,
  tripdetaileddayserviceid,
  isPreview,
  isDesktop
}) => {
  const { t } = useTranslation(['services']);
  const { timeFormat } = useTimeFormat();

  //Preview Content
  if (isPreview) {
    return (
      <div>
        <div className='activity-card__header'>
          <h3 className='activity-card__title'>{servicefields?.TITLE || ''}</h3>
        </div>
        {servicefields?.DEPARTURETIME && (
          <div className='activity-card__time'>
            <Clock size={14} weight='regular' />
            <span>
              {formatTimeLocale(servicefields?.DEPARTURETIME, timeFormat)}
            </span>
          </div>
        )}

        <div className='activity-card__description-container'>
          {servicefields?.AIRLINE || servicefields?.FLIGHTNUMBER ? (
            <div className='activity-card__description'>
              <p className='activity-card__description'>
                {servicefields?.AIRLINE || ''}{' '}
                {servicefields?.FLIGHTNUMBER || ''}
              </p>
            </div>
          ) : null}

          {servicefields?.CONFIRMATIONINFORMATION && (
            <div className='activity-card__confirmation'>
              {t('services:confirmationLabel', 'Confirmation')}:{' '}
              {servicefields?.CONFIRMATIONINFORMATION || ''}
            </div>
          )}
        </div>

        {isDesktop && (
          <>
            <div className='activity-card__desktop-preview'>
              <div className='activity-card__modal-field'>
                {servicefields?.DEPARTURETIME ||
                servicefields.DEPARTUREAIRPORT ? (
                  <p className='activity-card__modal-field__title'>
                    {t('services:departure', 'Departure')}
                  </p>
                ) : null}

                {servicefields.DEPARTURETIME && (
                  <div className='activity-card__modal-field__item'>
                    <Clock size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {formatTimeLocale(
                        servicefields.DEPARTURETIME,
                        timeFormat
                      )}
                    </p>
                  </div>
                )}

                {servicefields.DEPARTUREAIRPORT && (
                  <div className='activity-card__modal-field__item'>
                    <MapPin size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      role='button'
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMaps(servicefields.DEPARTUREAIRPORT);
                      }}
                      title={t('services:openInMaps', 'Open in Maps')}
                    >
                      <span className='selectable-link'>
                        {servicefields?.DEPARTUREAIRPORT || ''}
                      </span>
                    </a>
                  </div>
                )}
              </div>

              <div className='activity-card__modal-field'>
                {servicefields?.ARRIVALTIME || servicefields?.ARRIVALAIRPORT ? (
                  <p className='activity-card__modal-field__title'>
                    {t('services:arrival', 'Arrival')}
                  </p>
                ) : null}

                {servicefields?.ARRIVALTIME && (
                  <div className='activity-card__modal-field__item'>
                    <Clock size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {formatTimeLocale(servicefields.ARRIVALTIME, timeFormat)}
                    </p>
                  </div>
                )}

                {servicefields?.ARRIVALAIRPORT && (
                  <div className='activity-card__modal-field__item'>
                    <MapPin size={16} color='#667387' />
                    <a
                      className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                      role='button'
                      tabIndex={0}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        openMaps(servicefields.ARRIVALAIRPORT);
                      }}
                      title={t('services:openInMaps', 'Open in Maps')}
                    >
                      <span className='selectable-link'>
                        {servicefields?.ARRIVALAIRPORT || ''}
                      </span>
                    </a>
                  </div>
                )}
              </div>

              {servicefields?.FLIGHTNUMBER && (
                <div className='activity-card__modal-field'>
                  <p className='activity-card__modal-field__title'>
                    {t('services:flightNumber', 'Flight Number')}
                  </p>
                  <div className='activity-card__modal-field__item'>
                    <AirplaneTakeoff size={16} color='#667387' />
                    <p className='activity-card__modal-field__value'>
                      {servicefields.FLIGHTNUMBER || ''}
                    </p>
                  </div>
                </div>
              )}
            </div>

            {servicefields?.DESCRIPTION && (
              <div className='activity-card__modal-rich-text'>
                <Editor
                  id={`service-editor-${tripdetaileddayserviceid}-description`}
                  content={servicefields?.DESCRIPTION}
                  editable={false}
                  isPreviewing={true}
                />
              </div>
            )}
            {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
              <div className='activity-card__modal-field'>
                <p className='activity-card__modal-field__title'>
                  {t('services:attachedDocument', 'Attached Document')}
                </p>
                <div>
                  {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                    <a
                      key={idx}
                      href={attachment?.url}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='activity-card__attachment-link'
                      onClick={(e) => {
                        e.stopPropagation();
                      }}
                    >
                      <FileText size={20} />
                      <span>{attachment?.originalName}</span>
                    </a>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  //Modal Content
  return (
    <div id={`service-card-flights-${tripdetaileddayserviceid}`}>
      <div>
        {servicefields?.CONFIRMATIONINFORMATION && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:confirmationLabel', 'Confirmation')}
            </p>
            <div className='activity-card__modal__confirmation'>
              {servicefields?.CONFIRMATIONINFORMATION || ''}
            </div>
          </div>
        )}

        {servicefields?.AIRLINE && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:provider', 'Provider')}
            </p>
            <p className='activity-card__modal-field__value'>
              {servicefields?.AIRLINE || ''}
            </p>
          </div>
        )}

        <div className='activity-card__modal-field'>
          {servicefields?.DEPARTURETIME || servicefields.DEPARTUREAIRPORT ? (
            <p className='activity-card__modal-field__title'>
              {t('services:departure', 'Departure')}
            </p>
          ) : null}

          {servicefields.DEPARTURETIME && (
            <div className='activity-card__modal-field__item'>
              <Clock size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {formatTimeLocale(servicefields.DEPARTURETIME, timeFormat)}
              </p>
            </div>
          )}

          {servicefields.DEPARTUREAIRPORT && (
            <div className='activity-card__modal-field__item'>
              <MapPin size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openMaps(servicefields.DEPARTUREAIRPORT);
                }}
                title={t('services:openInMaps', 'Open in Maps')}
              >
                <span className='selectable-link'>
                  {servicefields?.DEPARTUREAIRPORT || ''}
                </span>
              </a>
            </div>
          )}
        </div>

        <div className='activity-card__modal-field'>
          {servicefields?.ARRIVALTIME || servicefields?.ARRIVALAIRPORT ? (
            <p className='activity-card__modal-field__title'>
              {t('services:arrival', 'Arrival')}
            </p>
          ) : null}

          {servicefields?.ARRIVALTIME && (
            <div className='activity-card__modal-field__item'>
              <Clock size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {formatTimeLocale(servicefields.ARRIVALTIME, timeFormat)}
              </p>
            </div>
          )}

          {servicefields?.ARRIVALAIRPORT && (
            <div className='activity-card__modal-field__item'>
              <MapPin size={16} color='#667387' />
              <a
                className='activity-card__modal-field__value activity-card__modal-field__value--phone'
                role='button'
                tabIndex={0}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openMaps(servicefields.ARRIVALAIRPORT);
                }}
                title={t('services:openInMaps', 'Open in Maps')}
              >
                <span className='selectable-link'>
                  {servicefields?.ARRIVALAIRPORT || ''}
                </span>
              </a>
            </div>
          )}
        </div>

        {servicefields?.FLIGHTNUMBER && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:flightNumber', 'Flight Number')}
            </p>
            <div className='activity-card__modal-field__item'>
              <AirplaneTakeoff size={16} color='#667387' />
              <p className='activity-card__modal-field__value'>
                {servicefields.FLIGHTNUMBER || ''}
              </p>
            </div>
          </div>
        )}

        {servicefields?.UPLOADATTACHMENTS?.length > 0 && (
          <div className='activity-card__modal-field'>
            <p className='activity-card__modal-field__title'>
              {t('services:attachedDocument', 'Attached Document')}
            </p>
            <div>
              {servicefields?.UPLOADATTACHMENTS?.map((attachment, idx) => (
                <a
                  key={idx}
                  href={attachment?.url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='activity-card__attachment-link'
                >
                  <FileText size={20} />
                  <span>{attachment?.originalName}</span>
                </a>
              ))}
            </div>
          </div>
        )}

        {servicefields?.DESCRIPTION && (
          <div className='activity-card__modal-rich-text'>
            <Editor
              id={`service-editor-${tripdetaileddayserviceid}-description`}
              content={servicefields?.DESCRIPTION}
              editable={false}
              isPreviewing={true}
            />
          </div>
        )}
      </div>
    </div>
  );
};

FlightCard.propTypes = {
  servicefields: PropTypes.object,
  servicetype: PropTypes.object,
  tripdetaileddayserviceid: PropTypes.number,
  isPreview: PropTypes.bool,
  isDesktop: PropTypes.bool
};

export default FlightCard;
